/**
 * Hook pour récupérer les permissions en masse pour plusieurs modèles
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import api from '@services/api';
import { ModelType, ActionType } from '@constants/authorization';

/**
 * Hook pour récupérer les permissions en masse pour plusieurs modèles
 *
 * @param {Array<Object>} models - Liste des modèles avec leurs model_type
 * @param {Array<string>} actions - Actions à vérifier (optionnel)
 * @returns {Object} - { permissions, loading, error, refetch }
 */
const useBulkPermissions = (models, actionTypes) => {
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use refs to track previous values for comparison
  const prevModelsRef = useRef();
  const prevActionTypesRef = useRef();
  const isFetchingRef = useRef(false); // Prevent concurrent fetches

  // Helper function to compare arrays
  const arraysEqual = (a, b) => {
    if (!a || !b) return a === b;
    if (a.length !== b.length) return false;
    return a.every((val, idx) => val === b[idx]);
  };

  // Extract and group models by their model_type
  const modelsByType = useMemo(() => {
    if (!Array.isArray(models) || models.length === 0) return {};

    return models.reduce((acc, model) => {
      if (!model?.id || !model?.model_type) return acc;

      if (!acc[model.model_type]) {
        acc[model.model_type] = [];
      }
      acc[model.model_type].push(model.id);

      return acc;
    }, {});
  }, [models]);

  // Create a serializable representation for comparison
  const modelsKey = useMemo(() => {
    return JSON.stringify(modelsByType);
  }, [modelsByType]);

  // Check if dependencies have actually changed
  const dependenciesChanged = useMemo(() => {
    const modelsChanged = prevModelsRef.current !== modelsKey;
    const actionTypesChanged = !arraysEqual(prevActionTypesRef.current, actionTypes);

    // Update refs with current values
    prevModelsRef.current = modelsKey;
    prevActionTypesRef.current = actionTypes;

    return modelsChanged || actionTypesChanged;
  }, [modelsKey, actionTypes]);

  useEffect(() => {
    if (!dependenciesChanged) {
      return;
    }

    if (!models || models.length === 0 || Object.keys(modelsByType).length === 0) {
      setPermissions({});
      setLoading(false);
      return;
    }

    const fetchPermissions = async () => {
      // Prevent concurrent fetches
      if (isFetchingRef.current) {
        return;
      }

      try {
        isFetchingRef.current = true;
        setLoading(true);

        // Convert model_type to backend ModelType constants
        const getBackendModelType = (modelType) => {
          switch (modelType) {
            case 'resource': return ModelType.RESOURCE;
            case 'category': return ModelType.CATEGORY;
            case 'location': return ModelType.LOCATION;
            case 'borrowing': return ModelType.BORROWING;
            case 'consumption': return ModelType.CONSUMPTION;
            case 'refill': return ModelType.REFILL;
            case 'tag': return ModelType.TAG;
            case 'user': return ModelType.USER;
            default: return null;
          }
        };

        // Fetch permissions for each model type
        const permissionPromises = Object.entries(modelsByType).map(async ([modelType, modelIds]) => {
          const backendModelType = getBackendModelType(modelType);
          if (!backendModelType) return { modelIds: [], permissions: {} };

          const requestData = {
            model_ids: [...modelIds].sort(), // Sort for consistency
            model_type: backendModelType,
            action_types: actionTypes || [ActionType.UPDATE, ActionType.DELETE]
          };

          const result = await api.authorization.bulkPermissions(requestData);
          return { modelIds, permissions: result.data.permissions || {} };
        });

        const results = await Promise.all(permissionPromises);

        // Merge all permissions into a single object
        const allPermissions = {};
        results.forEach(({ permissions }) => {
          Object.assign(allPermissions, permissions);
        });

        setPermissions(allPermissions);
        setError(null);
      } catch (err) {
        console.error('Error fetching bulk permissions:', err);
        setError(err);
        setPermissions({});
      } finally {
        setLoading(false);
        isFetchingRef.current = false;
      }
    };

    fetchPermissions();
  }, [dependenciesChanged, models, modelsByType, actionTypes]);

  return { permissions, loading, error };
};

/**
 * Hook spécialisé pour les permissions en masse sur les ressources
 */
export const useBulkResourcePermissions = (resources) => {
  return useBulkPermissions(resources, [
    ActionType.READ,
    ActionType.UPDATE,
    ActionType.DELETE,
    ActionType.BORROW,
    ActionType.CONSUME,
    ActionType.REFILL,
    ActionType.RESERVE
  ]);
};

/**
 * Hook spécialisé pour les permissions en masse sur les catégories
 */
export const useBulkCategoryPermissions = (categories) => {
  return useBulkPermissions(categories, [
    ActionType.READ,
    ActionType.UPDATE,
    ActionType.DELETE
  ]);
};

/**
 * Hook spécialisé pour les permissions en masse sur les locations
 */
export const useBulkLocationPermissions = (locations) => {
  return useBulkPermissions(locations, [
    ActionType.READ,
    ActionType.UPDATE,
    ActionType.DELETE
  ]);
};

/**
 * Hook utilitaire pour extraire les IDs depuis une liste de modèles
 */
export const useModelIds = (models) => {
  return useMemo(() => {
    if (!Array.isArray(models)) return [];
    return models.map(model => model?.id).filter(id => id !== undefined && id !== null);
  }, [models]);
};

export default useBulkPermissions;
